/**
 * End-of-match awards.
 *
 * Shared by the server (which owns the online numbers) and the offline bomb
 * mode (which owns its own), because two implementations of "who did the most
 * damage" would eventually disagree about ties, about zero, or about whether a
 * bot can win one — and the whole point of an award is that it settles that.
 *
 * ## What is an award worth
 *
 * Every award needs a floor. Without one, "MARKSMAN — 3 m" appears in a match
 * where nobody took a long shot, and an award nobody earned is worse than no
 * award: it makes the real ones look arbitrary too. A category with no
 * qualifying player is simply not shown.
 *
 * Ties go to whoever appears first in the row order, which is the order the
 * players joined. Arbitrary, but stable and identical on both machines — which
 * matters more than being clever, because everyone must see the same screen.
 */

/**
 * @typedef {{
 *   id:string, name:string, team:'red'|'blue', op:number, bot?:boolean,
 *   kills:number, deaths:number, headshots:number, damage:number, dist:number,
 *   plants:number, defuses:number, shots:number, hits:number,
 *   longestKill:number, bestStreak:number,
 * }} AwardRow
 */

/** Every award, in the order the screens are shown. */
export const AWARDS = [
  {
    id: 'mvp',
    title: 'MOST VALUABLE',
    sub: 'best all-round match',
    /**
     * Not kills. A player who traded 20 for 20 did less for their side than one
     * who took 14 and died 4 times, and objective work does not show up in a
     * kill count at all — so a plant or a defuse is worth about two kills here.
     */
    score: (r) => r.kills * 2 + (r.plants + r.defuses) * 4 + r.damage / 100 - r.deaths,
    min: (r) => r.kills > 0 || r.plants > 0 || r.defuses > 0,
    stat: (r) => `${r.kills} KILLS · ${r.deaths} DEATHS`,
  },
  {
    id: 'kills',
    title: 'TOP OPERATOR',
    sub: 'most eliminations',
    score: (r) => r.kills,
    min: (r) => r.kills >= 2,
    stat: (r) => `${r.kills} KILLS`,
  },
  {
    id: 'headshots',
    title: 'HEADHUNTER',
    sub: 'most headshots',
    score: (r) => r.headshots,
    min: (r) => r.headshots >= 2,
    stat: (r) => `${r.headshots} HEADSHOTS`,
  },
  {
    id: 'damage',
    title: 'HEAVY HITTER',
    sub: 'most damage dealt',
    score: (r) => r.damage,
    min: (r) => r.damage >= 200,
    stat: (r) => `${Math.round(r.damage)} DAMAGE`,
  },
  {
    id: 'marksman',
    title: 'LONG SHOT',
    sub: 'furthest kill',
    score: (r) => r.longestKill,
    // Anything under 25 m is a normal fight, not a long shot.
    min: (r) => r.longestKill >= 25,
    stat: (r) => `${Math.round(r.longestKill)} M`,
  },
  {
    id: 'streak',
    title: 'RAMPAGE',
    sub: 'longest life, by bodies',
    score: (r) => r.bestStreak,
    min: (r) => r.bestStreak >= 3,
    stat: (r) => `${r.bestStreak} IN A ROW`,
  },
  {
    id: 'plants',
    title: 'DEMOLITIONS',
    sub: 'most bombs planted',
    score: (r) => r.plants,
    min: (r) => r.plants >= 1,
    stat: (r) => `${r.plants} PLANTED`,
  },
  {
    id: 'defuses',
    title: 'BOMB TECH',
    sub: 'most bombs defused',
    score: (r) => r.defuses,
    min: (r) => r.defuses >= 1,
    stat: (r) => `${r.defuses} DEFUSED`,
  },
  {
    id: 'accuracy',
    title: 'PRECISION',
    sub: 'best accuracy',
    score: (r) => (r.shots >= 20 ? r.hits / r.shots : 0),
    // Twenty shots is the smallest sample that is not luck.
    min: (r) => r.shots >= 20 && r.hits > 0,
    stat: (r) => `${Math.round((r.hits / Math.max(1, r.shots)) * 100)}% HITS`,
  },
  {
    id: 'distance',
    title: 'LONG RUNNER',
    sub: 'most ground covered',
    score: (r) => r.dist,
    min: (r) => r.dist >= 300,
    stat: (r) => `${(r.dist / 1000).toFixed(2)} KM`,
  },
];

/** Fill in anything a caller left off, so `score` never sees undefined. */
export function normaliseRow(r) {
  return {
    id: String(r.id ?? ''),
    name: r.name ?? '—',
    team: r.team === 'blue' ? 'blue' : 'red',
    op: Number.isFinite(r.op) ? r.op : 0,
    bot: !!r.bot,
    kills: r.kills ?? 0,
    deaths: r.deaths ?? 0,
    headshots: r.headshots ?? 0,
    damage: r.damage ?? 0,
    dist: r.dist ?? 0,
    plants: r.plants ?? 0,
    defuses: r.defuses ?? 0,
    shots: r.shots ?? 0,
    hits: r.hits ?? 0,
    longestKill: r.longestKill ?? 0,
    bestStreak: r.bestStreak ?? 0,
  };
}

/**
 * Pick a winner for every award that anybody qualified for.
 *
 * @param {AwardRow[]} rows
 * @param {{max?:number}} opts `max` caps how many screens are produced; the
 *   list is already in priority order, so this trims from the least
 *   interesting end.
 * @returns {{id, title, sub, stat, winner: AwardRow, runners: AwardRow[]}[]}
 */
export function computeAwards(rows, { max = 6 } = {}) {
  const list = rows.map(normaliseRow);
  const out = [];
  for (const a of AWARDS) {
    const eligible = list.filter((r) => a.min(r));
    if (!eligible.length) continue;
    const ranked = [...eligible].sort((x, y) => a.score(y) - a.score(x));
    const winner = ranked[0];
    if (!(a.score(winner) > 0)) continue;
    out.push({
      id: a.id,
      title: a.title,
      sub: a.sub,
      stat: a.stat(winner),
      winner,
      // Two runners-up, so a screen says who nearly had it.
      runners: ranked.slice(1, 3).map((r) => ({ name: r.name, team: r.team, stat: a.stat(r) })),
    });
    if (out.length >= max) break;
  }
  return out;
}
